import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useListGoogleCalendars,
  useUpdateCalendarSelection,
} from '@/features/integrations/api';
import type { CalendarAccount } from '@/features/integrations/types';
import { useSession } from '@/lib/session';
import { useEffect, useMemo, useState } from 'react';
import { IntegrationSettingsDialog } from './integration-settings-dialog';

interface GoogleCalendarSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect: () => void;
  onDisconnect: (accountId: string) => void;
  accounts: CalendarAccount[];
}

const CREATE_NEW_CALENDAR = '__create_new_calendar__';

export function GoogleCalendarSettingsDialog({
  open,
  onOpenChange,
  onConnect,
  onDisconnect,
  accounts,
}: GoogleCalendarSettingsDialogProps) {
  const { data: session } = useSession();
  const newestAccount = useMemo(
    () =>
      [...accounts].sort((a, b) => {
        const ownA = a.userId === session?.user?.id ? 1 : 0;
        const ownB = b.userId === session?.user?.id ? 1 : 0;
        return ownB - ownA || b.createdAt.localeCompare(a.createdAt);
      })[0],
    [accounts, session?.user?.id]
  );
  const [accountId, setAccountId] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [newCalendarName, setNewCalendarName] = useState('Borrah');
  const [syncScope, setSyncScope] = useState<'organization' | 'personal'>(
    'organization'
  );
  const account = accounts.find((item) => item.id === accountId);
  const ownsAccount = !!account && account.userId === session?.user?.id;
  const accountOwnerName =
    account?.connectedByName?.trim() || account?.email || 'This account';
  const {
    calendars,
    isLoading: isLoadingCalendars,
    isError: hasCalendarError,
    refetch: refetchCalendars,
  } = useListGoogleCalendars(ownsAccount ? accountId : '');
  const { updateSelectionAsync, isUpdating } = useUpdateCalendarSelection({
    onSuccess: () => onOpenChange(false),
  });

  useEffect(() => {
    if (!open) return;
    setAccountId(newestAccount?.id ?? '');
    setCalendarId(newestAccount?.calendarId ?? '');
    setNewCalendarName('Borrah');
    setSyncScope(newestAccount?.syncScope ?? 'organization');
  }, [
    open,
    newestAccount?.id,
    newestAccount?.calendarId,
    newestAccount?.syncScope,
  ]);

  const selectAccount = (id: string) => {
    setAccountId(id);
    const selected = accounts.find((item) => item.id === id);
    setCalendarId(selected?.calendarId ?? '');
    setNewCalendarName('Borrah');
    setSyncScope(selected?.syncScope ?? 'organization');
  };

  const save = () => {
    if (!ownsAccount || !account || !calendarId) return;
    const creatingCalendar = calendarId === CREATE_NEW_CALENDAR;
    if (
      !needsActivation &&
      !creatingCalendar &&
      calendarId === account.calendarId &&
      syncScope === account.syncScope
    ) {
      onOpenChange(false);
      return;
    }
    if (creatingCalendar && !newCalendarName.trim()) return;
    void updateSelectionAsync({
      accountId,
      ...(creatingCalendar
        ? { newCalendarName: newCalendarName.trim() }
        : { calendarId }),
      syncScope,
      activate: needsActivation || syncScope !== account.syncScope,
    }).catch(() => {
      // The mutation displays the error and keeps the dialog open.
    });
  };

  const unchanged =
    calendarId === account?.calendarId && syncScope === account?.syncScope;
  const creatingCalendar = calendarId === CREATE_NEW_CALENDAR;
  const needsActivation =
    !!account &&
    (!account.selected ||
      !account.syncEnabled ||
      accounts.some(
        (item) =>
          item.id !== account.id &&
          item.userId === account.userId &&
          item.syncScope === syncScope &&
          item.syncEnabled
      ));

  return (
    <IntegrationSettingsDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Google Calendar + Meet"
      description="Sync your Google Calendar and create Meet links for online-only services."
      icon={<img src="/google-calendar-icon.svg" alt="" className="size-10" />}
      onSave={ownsAccount ? save : undefined}
      saveLabel={
        needsActivation
          ? 'Use this Google account'
          : unchanged
            ? 'Done'
            : 'Save settings'
      }
      saveDisabled={
        !account ||
        !calendarId ||
        (creatingCalendar && !newCalendarName.trim()) ||
        ((!unchanged || needsActivation) &&
          !creatingCalendar &&
          (isLoadingCalendars || hasCalendarError))
      }
      isSaving={isUpdating}
      onDisconnect={ownsAccount ? () => onDisconnect(account.id) : undefined}
    >
      <p className="text-sm text-muted-foreground">
        Choose whether this Google account reflects its owner's diary or the
        whole organisation. Google events appear as busy time in Borrah.
      </p>
      {!ownsAccount && account && (
        <p className="text-sm text-muted-foreground">
          Only {accountOwnerName} can change or disconnect this Google account.
        </p>
      )}
      {ownsAccount && !account.syncEnabled && (
        <p className="text-sm text-muted-foreground" role="status">
          This account is connected but is not syncing yet. Choose a calendar
          and select Use this Google account to finish setup.
        </p>
      )}

      <Field>
        <FieldLabel>Google account</FieldLabel>
        <Select value={accountId} onValueChange={selectAccount}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select an account" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.email}
                {item.selected && item.syncEnabled ? ' (Syncing)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field>
        <FieldLabel>Sync whose bookings?</FieldLabel>
        <Select
          value={syncScope}
          disabled={!ownsAccount}
          onValueChange={(value) =>
            setSyncScope(value as 'organization' | 'personal')
          }
        >
          <SelectTrigger className="w-full" aria-label="Sync scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="personal"
              disabled={account?.userId !== session?.user?.id}
            >
              {accountOwnerName}
            </SelectItem>
            <SelectItem value="organization">Everything</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {account?.userId !== session?.user?.id && syncScope !== 'personal'
            ? 'Only the account owner can select their personal diary.'
            : syncScope === 'personal'
              ? `Only appointments assigned to ${accountOwnerName} sync to Google. Google events block their availability.`
              : 'Bookings without a staff-specific calendar sync here. Each Google event shows who the booking is assigned to. Google events block availability across the organisation.'}
        </p>
      </Field>

      {ownsAccount && (
        <Field>
          <FieldLabel>Calendar to sync</FieldLabel>
          <Select
            value={calendarId}
            onValueChange={setCalendarId}
            disabled={isLoadingCalendars}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  isLoadingCalendars
                    ? 'Loading calendars…'
                    : 'Select a calendar'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {calendars.map((calendar) => (
                <SelectItem key={calendar.id} value={calendar.id}>
                  {calendar.primary
                    ? `Main calendar (${calendar.summary})`
                    : calendar.summary}
                </SelectItem>
              ))}
              <SelectItem value={CREATE_NEW_CALENDAR}>
                Create a new calendar…
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            New connections use your main Google calendar. Create a separate
            calendar here if you prefer to keep Borrah events apart.
          </p>
          {creatingCalendar && (
            <div className="space-y-2">
              <FieldLabel htmlFor="new-google-calendar-name">
                New calendar name
              </FieldLabel>
              <Input
                id="new-google-calendar-name"
                value={newCalendarName}
                maxLength={100}
                onChange={(event) => setNewCalendarName(event.target.value)}
                placeholder="Borrah"
              />
            </div>
          )}
          {hasCalendarError && (
            <div
              className="flex items-center gap-2 text-sm text-destructive"
              role="alert"
            >
              Could not load calendars.
              <Button
                type="button"
                variant="link"
                className="h-auto p-0"
                onClick={() => void refetchCalendars()}
              >
                Try again
              </Button>
            </div>
          )}
          {ownsAccount &&
            !hasCalendarError &&
            !isLoadingCalendars &&
            calendars.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No writable calendars were found for this account.
              </p>
            )}
        </Field>
      )}

      {account?.lastSyncAt && (
        <p className="text-xs text-muted-foreground">
          Last synced {new Date(account.lastSyncAt).toLocaleString()}
        </p>
      )}

      <Button
        type="button"
        variant="link"
        className="h-auto self-start p-0"
        onClick={onConnect}
      >
        Connect another Google account
      </Button>
    </IntegrationSettingsDialog>
  );
}
